import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Twitter, Instagram, Linkedin, Mail, Phone, MapPin, Github, DollarSign, LineChart, Users, Building, Sparkles, Box, Home } from 'lucide-react';
import { useBrand } from '@/lib/brand';

const Footer = () => {
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const brand = useBrand();

  // Get brand logo icon
  const getBrandIcon = () => {
    // Check if brand has a custom logo URL first
    if (brand.logo.url) {
      return <img src={brand.logo.url} alt={brand.name} className="h-8 w-auto object-contain" />;
    }
    
    if (brand.logo.icon === 'SparklesIcon') {
      return <Sparkles className="h-8 w-auto" />;
    } else if (brand.logo.icon === 'BoxIcon') {
      return <Box className="h-8 w-auto" />;
    } else if (brand.logo.icon === 'HomeIcon') {
      return <Home className="h-8 w-auto" />;
    }
    return <img src="/images/favicon.png" alt={brand.name} className="h-8 w-auto" />;
  };

  const navigation = {
    main: [
      { name: 'Dashboard', href: '/' },
      { name: 'About', href: '/about' },
      { name: 'Features', href: '/features' },
      { name: 'Pricing', href: '/pricing' },
      { name: 'Blog', href: '/blog' },
      { name: 'Contact', href: '/contact' },
    ],
    solutions: [
      { name: 'Accounting', href: '/', icon: DollarSign },
      { name: 'Sales', href: '/', icon: LineChart },
      { name: 'CRM', href: '/', icon: Users },
      { name: 'Business Management', href: '/businesses', icon: Building },
    ],
    social: [
      {
        name: 'Facebook',
        href: brand.social.facebook,
        icon: Facebook,
      },
      {
        name: 'Twitter',
        href: brand.social.twitter,
        icon: Twitter,
      },
      {
        name: 'LinkedIn',
        href: brand.social.linkedin,
        icon: Linkedin,
      },
      ...(brand.social.instagram ? [{
        name: 'Instagram',
        href: brand.social.instagram,
        icon: Instagram,
      }] : []),
    ],
  };

  return (
    <footer className="bg-white border-t border-border/40 mt-auto">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="py-8 md:flex md:items-center md:justify-between">
          <div className="mb-6 md:mb-0 max-w-md">
            <Link to="/" className="flex items-center gap-2">
              {getBrandIcon()}
              <span className="text-lg font-semibold text-primary">{brand.name}</span>
            </Link>
            <p className="mt-2 text-sm text-muted-foreground">
              {brand.description}
            </p>
            <div className="mt-4 flex space-x-6">
              {navigation.social.map((item) => (
                <a 
                  key={item.name} 
                  href={item.href} 
                  className="text-muted-foreground hover:text-primary transition-colors"
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <span className="sr-only">{item.name}</span>
                  <item.icon className="h-5 w-5" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>
          
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Navigation</h3>
              <ul className="mt-4 space-y-3">
                {navigation.main.map((item) => (
                  <li key={item.name}>
                    <Link 
                      to={item.href} 
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-foreground">Solutions</h3>
              <ul className="mt-4 space-y-3">
                {navigation.solutions.map((item) => (
                  <li key={item.name}>
                    <Link 
                      to={item.href} 
                      className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                      onClick={() => localStorage.setItem('currentView', item.name.toLowerCase())}
                    >
                      <item.icon className="h-4 w-4" />
                      <span>{item.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h3 className="text-sm font-semibold text-foreground">Contact</h3>
              <ul className="mt-4 space-y-3">
                <li>
                  <a 
                    href={`mailto:${brand.contact.email}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                  >
                    <Mail className="h-4 w-4" />
                    <span>{brand.contact.email}</span>
                  </a>
                </li>
                <li>
                  <a 
                    href={`tel:${brand.contact.phone}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                  >
                    <Phone className="h-4 w-4" />
                    <span>{brand.contact.phone}</span>
                  </a>
                </li>
                <li>
                  <a 
                    href={`https://wa.me/${brand.contact.whatsapp.replace(/[^0-9]/g, '')}`}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Phone className="h-4 w-4" />
                    <span>{brand.contact.whatsapp} (WhatsApp)</span>
                  </a>
                </li>
                <li>
                  <div className="text-sm text-muted-foreground flex items-center gap-2">
                    <MapPin className="h-4 w-4 flex-shrink-0" />
                    <span>{brand.contact.address}</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
        
        <div className="border-t border-border/40 py-4 md:flex md:items-center md:justify-between">
          <div className="text-xs text-muted-foreground">
            &copy; {currentYear} {brand.name}. All rights reserved.
          </div>
          <div className="mt-4 md:mt-0">
            <p className="text-xs text-muted-foreground">
              {brand.name === 'Thaton Realty' ? (
                <>
                  Powered by <a href="https://gerty.co.zw" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Gerty</a>
                </>
              ) : brand.name === 'Gerty' ? 'Built for Africa. Ready for the world.' : 
               'Built for Africa. Ready for the world.'}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;